import { useState, useRef, useEffect, useCallback } from 'react';

const useResponsiveTableHeight = ({
  minHeight = 220,
  bottomSpacing = 24,
  observeSelectors = []
} = {}) => {
  const [tableHeight, setTableHeight] = useState('420px');
  const tableSectionRef = useRef(null);
  const paginationRef = useRef(null);
  const frameRef = useRef(null);
  const lastHeightRef = useRef(null);

  const updateTableHeight = useCallback(() => {
    if (!tableSectionRef.current || !paginationRef.current) return;

    const tableTop = tableSectionRef.current.getBoundingClientRect().top;
    const paginationHeight = paginationRef.current.getBoundingClientRect().height;
    const availableHeight = Math.floor(window.innerHeight - tableTop - paginationHeight - bottomSpacing);
    const nextHeight = Math.max(minHeight, availableHeight);

    // Avoid redundant state updates that can trigger ResizeObserver loops.
    if (lastHeightRef.current !== nextHeight) {
      lastHeightRef.current = nextHeight;
      setTableHeight(`${nextHeight}px`);
    }
  }, [minHeight, bottomSpacing]);

  const scheduleHeightUpdate = useCallback(() => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      updateTableHeight();
      frameRef.current = null;
    });
  }, [updateTableHeight]);

  useEffect(() => {
    scheduleHeightUpdate();
    window.addEventListener('resize', scheduleHeightUpdate);

    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        scheduleHeightUpdate();
      });
      const paginationElement = paginationRef.current;

      observeSelectors.forEach((selector) => {
        const element = document.querySelector(selector);
        if (element) resizeObserver.observe(element);
      });

      if (paginationElement) resizeObserver.observe(paginationElement);
    }

    return () => {
      window.removeEventListener('resize', scheduleHeightUpdate);
      if (resizeObserver) resizeObserver.disconnect();
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [observeSelectors, scheduleHeightUpdate]);

  return {
    tableHeight,
    tableSectionRef,
    paginationRef,
    updateTableHeight
  };
};

export default useResponsiveTableHeight;
